import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { Link } from 'react-router-dom';

interface AnswerSheet {
    id: string;
    UserName: string;
    UserDisplayName?: string; // Added for display
    ShikakuName: string;
    TotalScore: number;
    CreateDate: any;
    UserRef?: any;
    OrderRef?: any;
    status?: string;
    orderNumber?: string;
    diploma?: string;
    orderId?: string;
    dStatus?: string;
}

export default function AnswerSheetManagement() {
    const [answers, setAnswers] = useState<AnswerSheet[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAnswers = async () => {
        setLoading(true);
        try {
            // Fetch last 100 results
            const q = query(collection(db, 'AnswerSheet'), orderBy('CreateDate', 'desc'), limit(100));
            const querySnapshot = await getDocs(q);

            // Fetch user display names for each answer sheet
            const list = await Promise.all(
                querySnapshot.docs.map(async (docSnapshot) => {
                    const data = docSnapshot.data();
                    let userDisplayName = data.UserName || '不明';

                    // Try to fetch display name from UserRef
                    if (data.UserRef) {
                        try {
                            const userDoc = await getDoc(data.UserRef);
                            if (userDoc.exists()) {
                                userDisplayName = userDoc.data().display_name || data.UserName || '不明';
                            }
                        } catch (error) {
                            console.error('Error fetching user display name:', error);
                        }
                    }

                    let orderNumber = '-';
                    let diploma = '-';
                    let orderId: string | undefined;
                    let dStatus: string | undefined;
                    if (data.OrderRef) {
                        try {
                            const orderDoc = await getDoc(data.OrderRef);
                            if (orderDoc.exists()) {
                                const orderData = orderDoc.data() as any;
                                orderNumber = orderData?.orderNumber || '-';
                                diploma = orderData?.diploma || '-';
                                orderId = orderDoc.id;
                                dStatus = orderData?.Dstatus;
                            }
                        } catch (error) {
                            console.error('Error fetching order data:', error);
                        }
                    }

                    return {
                        id: docSnapshot.id,
                        ...data,
                        UserDisplayName: userDisplayName,
                        orderNumber,
                        diploma,
                        orderId,
                        dStatus,
                    } as AnswerSheet;
                })
            );

            setAnswers(list);
        } catch (error) {
            console.error("Error fetching answers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnswers();
    }, []);

    const getStatusLabel = (status?: string) => {
        if (status === 'ready') {
            return { text: '発送待ち', className: 'bg-orange-400 text-white' };
        }
        if (status === 'done') {
            return { text: '完了', className: 'bg-gray-400 text-white' };
        }
        return { text: '-', className: 'bg-gray-200 text-gray-600' };
    };

    return (
        <div className="">
            <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <h1 className="text-2xl font-bold text-gray-800">回答結果管理</h1>
                    <Link
                        to="/admin/onlinetest"
                        className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        戻る
                    </Link>
                </div>
                <div className="mb-4 text-sm text-gray-600 font-medium">
                    現在の表示件数: {answers.length} 件
                </div>

                <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
                    <table className="w-full table-auto">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日時</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">注文番号</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">認定証</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">資格名</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">得点</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">状態</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">詳細</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={8} className="px-4 py-4 text-center">読み込み中...</td></tr>
                            ) : answers.map((answer) => {
                                const statusLabel = getStatusLabel(answer.dStatus);
                                return (
                                <tr key={answer.id}>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {answer.CreateDate?.toDate ? answer.CreateDate.toDate().toLocaleString('ja-JP') : '不明'}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{answer.orderNumber || '-'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{answer.diploma || '-'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{answer.UserDisplayName || answer.UserName}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{answer.ShikakuName}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{answer.TotalScore}点</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-3">
                                            <span
                                            className={`inline-flex rounded-full px-4 py-2 text-sm font-semibold ${statusLabel.className}`}
                                        >
                                                {statusLabel.text}
                                        </span>
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                onClick={() => {
                                                    if (answer.orderId) {
                                                        window.open(`/admin/orders/${answer.orderId}/exam?print=letter`, '_blank');
                                                    }
                                                }}
                                                disabled={!answer.orderId}
                                            >
                                                送付状
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            type="button"
                                            className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                            onClick={() => window.location.assign(`/admin/answers/${answer.id}`)}
                                        >
                                            詳細
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
